'use strict'

// Show or hide a login-protected page, a view, or a menu link from the logged-in user's row.
// The designer, the live app, and the app server share this match.
// A rule looks at that user's columns, at fields on records the user's
// relationships point to, and at fields on records that point at the user.

const { matchesDisplayCondition } = require('./display-rules')
const { pageRequiresLogin, pageRequiresLoginFlag } = require('./view-visibility')

function permissionRuleList (target) {

  const stored = target && target.userPermissionRules

  if (Array.isArray(stored)) return stored
  if (stored && Array.isArray(stored.rules)) return stored.rules

  return []
}

function hasUserPermissionRules (target) {

  return permissionRuleList(target).length > 0
}

function rulesApplyToPage (page, pages) {

  if (pages) return pageRequiresLogin(page, pages)

  return pageRequiresLoginFlag(page)
}

function rulesApplyToView (view, page, pages) {

  return !!(view && view.requiresLogin) || rulesApplyToPage(page, pages)
}

function castValue (value, dataType) {

  if (dataType === 'number') {

    const num = Number(value)

    return Number.isNaN(num) ? value : num
  }

  if (dataType === 'checkbox') {

    if (value === 'true' || value === true) return true
    if (value === 'false' || value === false) return false

    return !!value
  }

  return value
}

function valueMatches (testValue, condition) {

  let value = testValue
  const conditionValue = castValue(condition.value, condition.dataType)

  if (condition.dataType === 'number') value = castValue(value, 'number')
  else if (condition.dataType === 'checkbox') value = castValue(value, 'checkbox')

  return matchesDisplayCondition(value, condition.operator, conditionValue, condition.dataType)
}

function relatedKey (condition) {

  if (!condition) return ''

  if (condition.source === 'reverse') {

    return `reverse::${condition.sourceSpreadsheet || ''}::${condition.sourceRelationshipColumn || ''}`
  }

  return `${condition.relationshipColumn || ''}::${condition.relatedSpreadsheet || ''}`
}

function relatedRows (condition, related) {

  const rows = related && related[relatedKey(condition)]

  return Array.isArray(rows) ? rows : []
}

function relatedConditionMatches (condition, related) {

  if (!condition || !condition.field) return false

  if (condition.source === 'related' && !condition.relationshipColumn) return false
  if (condition.source === 'reverse' && (!condition.sourceSpreadsheet || !condition.sourceRelationshipColumn)) return false

  const rows = relatedRows(condition, related)
  const quantifier = condition.relatedQuantifier || 'any'

  if (!rows.length) {

    if (quantifier === 'none' || quantifier === 'all') return true

    return false
  }

  const matched = rows.map((row) => valueMatches(row && row[condition.field], condition))

  if (quantifier === 'all') return matched.every(Boolean)
  if (quantifier === 'none') return !matched.some(Boolean)

  return matched.some(Boolean)
}

function conditionMatches (condition, user, related) {

  if (!condition || condition.source === 'none') return true

  if (condition.source === 'related' || condition.source === 'reverse') {

    return relatedConditionMatches(condition, related)
  }

  if (condition.source !== 'field' || !condition.field) return false

  return valueMatches(user && user[condition.field], condition)
}

function conditionsMatch (conditions, matchMode, user, related) {

  const list = conditions || []

  if (!list.length) return true
  if (matchMode === 'any') return list.some((condition) => conditionMatches(condition, user, related))

  return list.every((condition) => conditionMatches(condition, user, related))
}

function ruleMatches (rule, user, related) {

  if (!rule) return false
  if (rule.type === 'else') return true

  const conditions = (rule.conditions || []).filter((condition) => condition && condition.source !== 'none')
  const always = (rule.conditions || []).some((condition) => condition && condition.source === 'none')

  if ((always && conditions.length === 0) || conditions.length === 0) return true

  return conditionsMatch(conditions, rule.conditionMatchMode || 'all', user, related)
}

function matchingPermissionAction (rules, user, related) {

  const list = Array.isArray(rules) ? rules : permissionRuleList({ userPermissionRules: rules })

  for (const rule of list) {

    if (!ruleMatches(rule, user, related)) continue

    // A matching rule hides unless it explicitly shows. A missing or mistyped
    // action must not leave the page visible.
    return rule.action === 'show' ? 'show' : 'hide'
  }

  return null
}

function userPermissionVisible (rules, user, related) {

  const list = Array.isArray(rules) ? rules : permissionRuleList({ userPermissionRules: rules })

  if (!list.length) return true
  if (!user) return false

  const action = matchingPermissionAction(list, user, related)

  return action === null || action === 'show'
}

function addLookupField (map, key, lookup, field) {

  if (!map.has(key)) map.set(key, lookup)

  if (field) map.get(key).fields.add(field)
}

function collectMenuPermissionRules (items) {

  const rules = []

  const walk = (list) => {

    for (const item of list || []) {

      if (!item || typeof item !== 'object') continue

      rules.push(...permissionRuleList(item))
      walk(item.children)
    }
  }

  walk(items)

  return rules
}

// A signed-out visitor keeps every link. Pass evaluate once a session exists
// so a link with rules is hidden when that person does not match.
function visibleMenuItems (items, user, related, options) {

  const evaluate = !!(options && options.evaluate)
  const next = []

  for (const item of items || []) {

    if (!item || typeof item !== 'object') continue

    const children = Array.isArray(item.children)
      ? visibleMenuItems(item.children, user, related, options)
      : null
    const rules = permissionRuleList(item)

    if (evaluate && item.type === 'link' && rules.length && !userPermissionVisible(rules, user, related)) continue

    const hadChildren = Array.isArray(item.children) && item.children.length > 0

    if (item.type === 'dropdown' && hadChildren && children && children.length === 0) continue

    const copy = { ...item }

    delete copy.userPermissionRules
    if (children) copy.children = children
    next.push(copy)
  }

  return next
}

function relatedLookups (rules) {

  const map = new Map()

  for (const rule of Array.isArray(rules) ? rules : []) {

    for (const condition of (rule && rule.conditions) || []) {

      if (!condition) continue

      if (condition.source === 'related' && condition.relationshipColumn && condition.relatedSpreadsheet) {

        const key = relatedKey(condition)

        addLookupField(map, key, {
          direction: 'forward',
          relationshipColumn: condition.relationshipColumn,
          relatedSpreadsheet: condition.relatedSpreadsheet,
          fields: new Set()
        }, condition.field)
      }

      if (condition.source === 'reverse' && condition.sourceSpreadsheet && condition.sourceRelationshipColumn) {

        const key = relatedKey(condition)

        addLookupField(map, key, {
          direction: 'reverse',
          sourceSpreadsheet: condition.sourceSpreadsheet,
          sourceRelationshipColumn: condition.sourceRelationshipColumn,
          fields: new Set()
        }, condition.field)
      }
    }
  }

  return Array.from(map.values())
}

module.exports = {
  permissionRuleList,
  hasUserPermissionRules,
  rulesApplyToPage,
  rulesApplyToView,
  relatedKey,
  relatedLookups,
  ruleMatches,
  matchingPermissionAction,
  userPermissionVisible,
  collectMenuPermissionRules,
  visibleMenuItems
}
